/**
 * Канбан заявок менеджера/админа («Облака» #34, дизайн manager v3 §6.1).
 *
 * Колонки по статусу: Новые → В работе → Выполнено → Отменено.
 * Drag&drop карточки между колонками = смена статуса заявки через
 * PATCH /v1/admin/requests/{id}/status (force-status) — менеджер может
 * перевести заявку в ЛЮБОЙ статус всегда (нет 412 при прямом/обратном
 * переходе). Индикатор SLA/просрочки на карточке. Фильтр по типу.
 *
 * Клик по карточке → панель ДЕТАЛЕЙ заявки (гость/номер/категория/статус/
 * история/переписка/смена статуса) + «Назад к доске».
 *
 * Ошибки — ТОСТ в правом верхнем углу (крестик + авто-скрытие), не нижний
 * баннер: при длинном списке заявок нижняя ошибка не видна.
 *
 * DnD-аффорданс (как Trello/Linear): при старте перетаскивания остальные
 * карточки приглушаются, целевые колонки подсвечиваются, drop-зона —
 * полная высота колонки (можно бросить в область существующих заявок).
 *
 * Desktop (≥768): 4 колонки, нативный HTML5 drag&drop.
 * Mobile (375): одна колонка + свайп-табы по статусу; смена статуса —
 * кнопками действий на карточке.
 *
 * i18n: все строки RU; сырые англ-коды state/категорий мапятся в русский.
 */
import { useMemo, useState } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import type { ManagerRequest } from '@hsc/types';
import {
  COLUMNS,
  TYPE_FILTERS,
  assigneeInitials,
  assigneeLabel,
  categoryLabel,
  columnOf,
  guestLabel,
  isAssigned,
  matchesType,
  priorityLabel,
  requestNumber,
  slaInfo,
  stateLabel,
  stayLabel,
  transitionFor,
  type ColumnKey,
  type TypeFilter,
} from './kanban-model.js';
import { useKanbanRequests, useMoveRequest } from './useRequestsKanban.js';
import { RequestDetailPanel } from './RequestDetailPanel.js';
import { CreateRequestForm } from './CreateRequestForm.js';
import { Toaster, useToasts } from './Toast.js';

interface TicketProps {
  req: ManagerRequest;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  dimmed: boolean;
}

/** Карточка заявки (тикет). Перетаскивается; клик — открыть детали. */
function Ticket({ req, onOpen, onDragStart, onDragEnd, dimmed }: TicketProps): JSX.Element {
  const sla = slaInfo(req);
  const col = columnOf(req.state);
  return (
    // Карточка одновременно кликабельна (открыть детали) и перетаскиваема
    // (сменить статус). role=button + Enter/Space дают клавиатурный
    // эквивалент клика; drag — десктоп-аффорданс. a11y-rule подавляем
    // осознанно (как и для drop-зон колонок ниже).
    /* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
    <li
      className={`rqk-ticket${sla.breached ? ' rqk-ticket--breached' : ''}${
        dimmed ? ' rqk-ticket--dimmed' : ''
      }`}
      data-testid={`kanban-ticket-${req.id}`}
      draggable
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', req.id);
        e.dataTransfer.setData('application/x-rqk-from', col);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      <div className="rqk-ticket__head">
        {/* Главный идентификатор — простой порядковый № заявки (#N). */}
        <span className="rqk-ticket__id" data-testid={`kanban-ticket-seq-${req.id}`}>
          Заявка {requestNumber(req)}
        </span>
        {sla.hasDeadline && (
          <span
            className={`rqk-chip${sla.breached ? ' rqk-chip--bad' : ''}`}
            data-testid={sla.breached ? `kanban-breached-${req.id}` : undefined}
          >
            {sla.breached ? '⚠ ' : ''}
            {sla.label}
          </span>
        )}
      </div>
      <div className="rqk-ticket__title">{req.title}</div>
      <div className="rqk-ticket__meta">
        {categoryLabel(req.category)} · {priorityLabel(req.priority)} · {stateLabel(req.state)}
      </div>
      {/* Гость + номер комнаты по проживанию (как в «Проживаниях»):
          фамилия гостя и №комнаты вместо uuid (правка Артёма). Строка
          появляется только при наличии данных. */}
      {(guestLabel(req) || stayLabel(req)) && (
        <div className="rqk-ticket__guest" data-testid={`kanban-ticket-guest-${req.id}`}>
          {guestLabel(req) && <span className="rqk-ticket__guest-name">{guestLabel(req)}</span>}
          {stayLabel(req) && <span className="rqk-ticket__room">{stayLabel(req)}</span>}
        </div>
      )}
      {/* Исполнитель на карточке (правка Артёма): аватар-инициалы + имя
          или «Не назначен». */}
      <div
        className={`rqk-ticket__assignee${isAssigned(req) ? '' : ' rqk-ticket__assignee--none'}`}
        data-testid={`kanban-ticket-assignee-${req.id}`}
      >
        <span className="rqk-ava" aria-hidden="true">
          {assigneeInitials(req)}
        </span>
        <span className="rqk-ticket__assignee-name">{assigneeLabel(req)}</span>
      </div>
    </li>
    /* eslint-enable jsx-a11y/no-noninteractive-element-to-interactive-role */
  );
}

export function RequestsKanbanPage(): JSX.Element {
  const [type, setType] = useState<TypeFilter>('all');
  const [mobileCol, setMobileCol] = useState<ColumnKey>('new');
  const [dragOver, setDragOver] = useState<ColumnKey | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError } = useKanbanRequests();
  const move = useMoveRequest();
  const toasts = useToasts();

  const requests = useMemo(() => data?.items ?? [], [data]);
  const filtered = useMemo(() => requests.filter((r) => matchesType(r, type)), [requests, type]);

  const byColumn = useMemo(() => {
    const map: Record<ColumnKey, ManagerRequest[]> = {
      new: [],
      in_progress: [],
      done: [],
      cancelled: [],
    };
    // Терминальные колонки (Выполнено/Отменено) — только завершённые сегодня и
    // вчера, иначе колонки разрастаются историей. Активные (Новые/В работе) — все.
    const termCutoff = (() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - 1);
      return d.getTime();
    })();
    for (const r of filtered) {
      const col = columnOf(r.state);
      if (col === 'done' || col === 'cancelled') {
        const ts = r.resolved_at ?? r.updated_at;
        if (!ts || new Date(ts).getTime() < termCutoff) continue;
      }
      map[col].push(r);
    }
    // Просроченные — наверх внутри колонки.
    for (const key of Object.keys(map) as ColumnKey[]) {
      map[key].sort((a, b) => {
        const sa = slaInfo(a).breached ? 0 : 1;
        const sb = slaInfo(b).breached ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return b.created_at.localeCompare(a.created_at);
      });
    }
    return map;
  }, [filtered]);

  const breachedCount = useMemo(() => filtered.filter((r) => slaInfo(r).breached).length, [filtered]);

  const openReq = useMemo(
    () => (openId ? (requests.find((r) => r.id === openId) ?? null) : null),
    [openId, requests],
  );

  function moveTo(req: ManagerRequest, to: ColumnKey): void {
    const target = transitionFor(columnOf(req.state), to);
    if (!target) return;
    move.mutate(
      { id: req.id, target },
      {
        onSuccess: () => toasts.success(`Статус заявки ${requestNumber(req)} изменён`),
        onError: () =>
          toasts.error(
            `Не удалось сменить статус заявки ${requestNumber(req)}. Попробуйте ещё раз.`,
          ),
      },
    );
  }

  function onDrop(to: ColumnKey, e: React.DragEvent): void {
    e.preventDefault();
    setDragOver(null);
    setDraggingId(null);
    const id = e.dataTransfer.getData('text/plain');
    const req = requests.find((r) => r.id === id);
    if (req) moveTo(req, to);
  }

  if (isLoading) {
    return (
      <section data-testid="kanban-page">
        <h1 className="hsc-page__title">Заявки · канбан</h1>
        <div className="rqk-board" data-testid="kanban-loading">
          {COLUMNS.map((c) => (
            <div key={c.key} className="rqk-col">
              <Skeleton height={160} radius={14} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section data-testid="kanban-page">
        <h1 className="hsc-page__title">Заявки · канбан</h1>
        <Card>
          <p role="alert">Не удалось загрузить заявки. Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }

  // Открыта форма создания заявки — рендерим её вместо доски.
  if (creating) {
    return (
      <>
        <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />
        <CreateRequestForm
          onClose={() => setCreating(false)}
          onCreated={(title) => toasts.success(`Заявка «${title}» создана`)}
        />
      </>
    );
  }

  // Открыта панель деталей заявки — рендерим её вместо доски.
  if (openReq) {
    return (
      <>
        <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />
        <RequestDetailPanel
          req={openReq}
          isMoving={move.isPending}
          onBack={() => setOpenId(null)}
          onMove={(to) => moveTo(openReq, to)}
        />
      </>
    );
  }

  const dragging = draggingId !== null;

  return (
    <section data-testid="kanban-page" className={dragging ? 'rqk-is-dragging' : undefined}>
      <Toaster toasts={toasts.toasts} onDismiss={toasts.dismiss} />

      <div className="rqk-head">
        <div className="rqk-head__titles">
          <h1 className="hsc-page__title">Заявки · канбан</h1>
          <p className="ta-muted" data-testid="kanban-summary">
            Всего: {filtered.length}
            {breachedCount > 0 && <> · просрочено по SLA: {breachedCount}</>}
          </p>
        </div>
        {/* «+ Заявка» — менеджер/владелец/персонал заводят заявку на любой
            номер/гостя (#34). Форма открывается поверх доски. */}
        <button
          type="button"
          className="rqk-new-btn"
          data-testid="kanban-new-request"
          onClick={() => setCreating(true)}
        >
          + Заявка
        </button>
      </div>

      <div className="ta-field rqk-filter">
        <label className="hsc-input__label" htmlFor="kanban-type-filter">
          Тип заявки
        </label>
        <select
          id="kanban-type-filter"
          className="ta-select"
          data-testid="kanban-type-filter"
          value={type}
          onChange={(e) => setType(e.target.value as TypeFilter)}
        >
          {TYPE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {/* Мобильные свайп-табы по колонкам (375 — manager v3 §6.1). */}
      <div className="rqk-swipe-tabs" role="tablist" data-testid="kanban-swipe-tabs">
        {COLUMNS.map((c) => (
          <button
            key={c.key}
            type="button"
            role="tab"
            aria-selected={mobileCol === c.key}
            className={`rqk-swipe-tab${mobileCol === c.key ? ' is-active' : ''}`}
            data-testid={`kanban-tab-${c.key}`}
            onClick={() => setMobileCol(c.key)}
          >
            {c.title} · {byColumn[c.key].length}
          </button>
        ))}
      </div>

      {/* Доска: desktop — 4 колонки drag&drop; mobile — активная колонка. */}
      <div className="rqk-board" data-testid="kanban-board">
        {COLUMNS.map((c) => {
          const items = byColumn[c.key];
          // Колонка-цель подсвечивается при перетаскивании (кроме исходной).
          const isDropTarget = dragging;
          return (
            // Колонка — drop-зона полной высоты (Trello/Linear-стиль).
            // Клавиатурный эквивалент — открытие карточки + кнопки в панели.
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              key={c.key}
              className={[
                'rqk-col',
                `rqk-col--${c.tone}`,
                dragOver === c.key ? 'is-dragover' : '',
                isDropTarget ? 'is-drop-target' : '',
                mobileCol === c.key ? 'is-mobile-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid={`kanban-col-${c.key}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOver !== c.key) setDragOver(c.key);
              }}
              onDragLeave={(e) => {
                // Уходим из колонки только если курсор реально покинул её область
                // (а не перешёл на дочерний элемент) — иначе подсветка моргает.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDragOver((cur) => (cur === c.key ? null : cur));
                }
              }}
              onDrop={(e) => onDrop(c.key, e)}
            >
              <h2 className="rqk-col__title">
                {c.title} <span className="rqk-col__count">{items.length}</span>
              </h2>
              {/* Drop-зона полной высоты колонки: список заявок + хвост. */}
              <ul className="rqk-col__list" data-testid={`kanban-dropzone-${c.key}`}>
                {items.length === 0 ? (
                  <li className="rqk-col__empty rqk-col__empty--drop">
                    {dragging ? 'Перетащите сюда' : 'Пусто'}
                  </li>
                ) : (
                  items.map((req) => (
                    <Ticket
                      key={req.id}
                      req={req}
                      dimmed={dragging && req.id !== draggingId}
                      onOpen={() => setOpenId(req.id)}
                      onDragStart={() => setDraggingId(req.id)}
                      onDragEnd={() => {
                        setDraggingId(null);
                        setDragOver(null);
                      }}
                    />
                  ))
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
